import { useState } from 'react'
import { isAxiosError } from 'axios'
import { ClipboardCheck, Loader2, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { MACHINERY_DIRECTIVE_STUDIO_ID } from '@artem-lex/common-server/complianceStudio'
import type { SupportedLanguage } from '@/shared/config/languages'
import {
  useCreateProductMachineryDirectiveCompliance,
  useGetProductComplianceStudios,
} from '@/shared/hooks/api/product'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
} from '@/components/ui/empty'
import { buildMachineryDirectiveStudioBaseHref } from '@/features/compliance-studio/machinery-directive/machineryDirectiveStudioPaths'
import { ProductComplianceStudioSelectDialog } from './ProductComplianceStudioSelectDialog'

type ProductComplianceStudiosPanelProps = {
  productId: string
  disableCreation?: boolean
}

type ComplianceStudioLinkProps = {
  to: string
  title: string
  completed: boolean
  completedLabel: string
  inProgressLabel: string
}

function ComplianceStudioLink({
  to,
  title,
  completed,
  completedLabel,
  inProgressLabel,
}: ComplianceStudioLinkProps) {
  return (
    <Link
      to={to}
      className='flex items-start gap-3 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/40 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none'
    >
      <ClipboardCheck
        className={cn(
          'mt-0.5 size-5 shrink-0',
          completed ? 'text-green-600 dark:text-green-500' : 'text-primary'
        )}
        aria-hidden
      />
      <span className='min-w-0 flex-1'>
        <span className='block text-sm font-medium'>{title}</span>
        <span className='mt-1 block text-xs text-muted-foreground'>
          {completed ? completedLabel : inProgressLabel}
        </span>
      </span>
    </Link>
  )
}

function productMachineryDirectiveStudioHref(
  productId: string,
  complianceId: string
): string {
  return buildMachineryDirectiveStudioBaseHref(productId, complianceId)
}

export function ProductComplianceStudiosPanel({
  productId,
  disableCreation = false,
}: ProductComplianceStudiosPanelProps) {
  const { t } = useTranslation('productCatalog')
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const shouldFetchStudios = !disableCreation
  const {
    machineryDirectiveCompliance,
    isLoading,
    isError,
    refetchMachineryDirective,
  } = useGetProductComplianceStudios(productId, shouldFetchStudios)
  const createMachineryDirectiveCompliance =
    useCreateProductMachineryDirectiveCompliance(productId)

  const hasAnyStudio = Boolean(machineryDirectiveCompliance)
  const showHeaderCreateButton = isLoading || isError || hasAnyStudio
  const isCreating = createMachineryDirectiveCompliance.isPending

  const completedLabel = t('detail.complianceStudios.completed')
  const inProgressLabel = t('detail.complianceStudios.inProgress')

  const openMachineryCompliance = (complianceId: string) => {
    navigate(productMachineryDirectiveStudioHref(productId, complianceId))
  }

  const handleCreateMachineryDirective = async (
    contentLanguage: SupportedLanguage
  ) => {
    if (disableCreation) {
      return
    }

    try {
      const compliance = await createMachineryDirectiveCompliance.mutateAsync({
        contentLanguage,
      })
      setDialogOpen(false)
      openMachineryCompliance(compliance.id)
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        const { data } = await refetchMachineryDirective()
        if (data?.id) {
          setDialogOpen(false)
          openMachineryCompliance(data.id)
          return
        }
      }
      toast.error(t('detail.complianceStudios.createError'))
    }
  }

  const handleStartStudio = async (
    studioId: string,
    options?: { contentLanguage: SupportedLanguage }
  ) => {
    if (disableCreation) {
      return
    }

    if (studioId !== MACHINERY_DIRECTIVE_STUDIO_ID) {
      toast.error(t('detail.complianceStudios.createError'))
      return
    }

    if (options?.contentLanguage == null) {
      toast.error(t('detail.complianceStudios.createError'))
      return
    }

    await handleCreateMachineryDirective(options.contentLanguage)
  }

  const studioLinks = machineryDirectiveCompliance ? (
    <div className='grid grid-cols-2 gap-2'>
      <ComplianceStudioLink
        to={productMachineryDirectiveStudioHref(
          productId,
          machineryDirectiveCompliance.id
        )}
        title={t('detail.complianceStudios.machineryDirectiveTitle')}
        completed={Boolean(machineryDirectiveCompliance.completed)}
        completedLabel={completedLabel}
        inProgressLabel={inProgressLabel}
      />
    </div>
  ) : null

  return (
    <>
      <Card className='flex min-w-0 flex-col'>
        <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-2 space-y-0 pb-4'>
          <CardTitle>{t('detail.complianceStudios.title')}</CardTitle>
          {showHeaderCreateButton ? (
            <Button
              size='sm'
              disabled={disableCreation}
              onClick={() => setDialogOpen(true)}
            >
              <Plus className='size-4' aria-hidden />
              {t('detail.complianceStudios.generateCta')}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className='flex flex-1 flex-col'>
          {isLoading ? (
            <div className='flex flex-1 items-center justify-center py-8'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          ) : isError ? (
            <Empty className='flex-1 border-0 py-2'>
              <EmptyHeader>
                <EmptyDescription>
                  {t('detail.complianceStudios.loadError')}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : hasAnyStudio ? (
            studioLinks
          ) : (
            <Empty className='flex-1 border-0 py-2'>
              <EmptyHeader>
                <EmptyDescription>
                  {t('detail.complianceStudios.empty')}
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button
                  size='sm'
                  disabled={disableCreation}
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className='size-4' aria-hidden />
                  {t('detail.complianceStudios.generateCta')}
                </Button>
              </EmptyContent>
            </Empty>
          )}
        </CardContent>
      </Card>
      <ProductComplianceStudioSelectDialog
        open={disableCreation ? false : dialogOpen}
        onOpenChange={setDialogOpen}
        productId={productId}
        onStartStudio={handleStartStudio}
        disableMachineryDirectiveOption={
          disableCreation || Boolean(machineryDirectiveCompliance)
        }
        isCreating={isCreating}
      />
    </>
  )
}
